export interface TrustedTypesHandler {
    policyName: string;
    getTrustedUrls: () => string[];
    trustUrl: (url: string) => string | TrustedScriptURL;
}

const handlers = new Map<string, TrustedTypesHandler>();

/**
 * Returns a Trusted Types handler for the given policy name.
 * If a handler for the given policy name already exists, it is returned.
 * Otherwise, a new handler is created and cached.
 *
 * Trusted Types policies exist on the window realm and duplicate policy names are not allowed.
 * When bootstrapCameraKit is called multiple times with the same trustedTypesPolicyName during
 * a window lifecycle, we reuse the cached handler instead of attempting to create a duplicate policy.
 *
 * @param trustedTypesPolicyName - The name of the Trusted Types policy to use.
 * @returns A Trusted Types handler for the given policy name.
 */
export function getTrustedTypesHandler(trustedTypesPolicyName: string) {
    const cached = handlers.get(trustedTypesPolicyName);
    if (cached) return cached;

    const handler = createHandler(trustedTypesPolicyName);
    handlers.set(trustedTypesPolicyName, handler);
    return handler;
}

function createHandler(trustedTypesPolicyName: string) {
    const trustedUrls = new Set<string>();
    const trustedTypes: TrustedTypePolicyFactory | undefined = (globalThis as any).trustedTypes;
    const policy = trustedTypes?.createPolicy(trustedTypesPolicyName, {
        createScriptURL: (url: string) => {
            if (trustedUrls.has(url)) return url;
            throw new TypeError("Blocked by Trusted Types: " + url);
        },
    });
    return {
        policyName: trustedTypesPolicyName,
        getTrustedUrls: () => [...trustedUrls],
        trustUrl: (url: string) => {
            trustedUrls.add(url);
            return policy?.createScriptURL(url) ?? url;
        },
    };
}
